import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcBalance, IcPdf, IcExcel, IcInfo, IcChevD, IcChevU } from "../../Icons";
import Header from "../../layout/Header/Header";
import { Button, ButtonGroup, Table, Input, Label } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeader, pdfFooter } from "../../../pdfUtils";
import axios from "axios";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// Balance Mensual V2
//
// Dos diferencias contra el balance original:
//
//   · SOCIEDAD — hasta 2024 el retiro se partía en dos; desde 2025 es completo.
//     El bloque de retiro se adapta solo al año que estés viendo.
//
//   · ANUALIDADES — los clientes que pagan el año por adelantado inflan enero.
//     El botón Caja / Devengado cambia entre "el dinero como cayó" y "el dinero
//     repartido entre los meses que cubre", incluso si la cobertura se pasa al
//     año siguiente. Por eso los totales de las dos vistas NO empatan: la
//     diferencia es (arrastre del año anterior − lo que se va al siguiente),
//     y se muestra explícita en la barra de conciliación.
// ─────────────────────────────────────────────────────────────────────────────

const MXN0 = new Intl.NumberFormat("es-MX", {
  style: "currency", currency: "MXN",
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});
const fMXN = (v) => MXN0.format(Number(v) || 0);
const fPct = (v) => `${((Number(v) || 0) * 100).toFixed(1)}%`;
const fCorto = (v) => {
  const n = Math.abs(Number(v) || 0);
  if (n >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${Math.round(v || 0)}`;
};
const z12 = () => Array(12).fill(0);
const suma = (a, idxs) => idxs.reduce((acc, i) => acc + (Number(a?.[i]) || 0), 0);
const safeDiv = (n, d) => (!d ? 0 : n / d);

const MES_CORTO = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function BalanceMensualV2() {
  const { user } = useContext(AuthContext);
  const URL_GRAFICAS = process.env.REACT_APP_URL_GRAFICAS;

  const anioInicio = 2021;
  const anioActual = new Date().getFullYear();
  const anios = Array.from({ length: anioActual - anioInicio + 1 }, (_, i) =>
    String(anioInicio + i)
  );

  const [selectedYear, setSelectedYear] = useState(String(anioActual));
  const [vista, setVista] = useState("devengado"); // "caja" | "devengado"
  const [incluirMesActual, setIncluirMesActual] = useState(true);
  const [verAnualidades, setVerAnualidades] = useState(false);
  const [data, setData] = useState(null);

  const esAnioEnCurso = selectedYear === String(anioActual);
  const fechaInicio = `${selectedYear}-01-01`;
  const fechaFin = esAnioEnCurso
    ? moment().format("YYYY-MM-DD")
    : `${selectedYear}-12-31`;

  useEffect(() => {
    let vigente = true;
    // Se limpia primero: si no, la tabla y las gráficas alcanzan a pintarse
    // con las series del año anterior mientras vuelve el fetch.
    setData(null);
    const params = incluirMesActual ? "" : "?incluirMesActual=false";
    axios
      .get(`${URL_GRAFICAS}/balanceMensualV2/${fechaInicio}/${fechaFin}${params}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => vigente && setData(r.data))
      .catch(() => vigente && setData(null));
    return () => { vigente = false; };
  }, [fechaInicio, fechaFin, incluirMesActual, URL_GRAFICAS]);

  // ── Meses que se muestran ─────────────────────────────────────────────────
  // Se cuentan sobre el año YA CARGADO, no sobre el seleccionado.
  const nVisibles =
    data?.anio === anioActual ? new Date().getMonth() + 1 : 12;
  const visibles = useMemo(
    () => Array.from({ length: nVisibles }, (_, i) => i),
    [nVisibles]
  );

  // ── Armado de filas ───────────────────────────────────────────────────────
  const calc = useMemo(() => {
    if (!data?.series) return null;
    const S = data.series;
    const devengado = vista === "devengado";

    const anualidades = devengado ? S.anualidadesDev : S.anualidades;
    const venta = z12().map(
      (_, i) => S.recurrente[i] + anualidades[i] + S.setup[i] + S.otros[i]
    );

    // Caja: nunca se prorratea. El retiro es dinero real.
    const utilidad = venta.map((v, i) => v - S.gastos[i]);
    const retiro = S.cobranza.map((c, i) => c - S.gastos[i]);
    const pSocio = data.sociedad.participacionSocio;
    const pMia = data.sociedad.participacionMia;
    const socio = retiro.map((r) => r * pSocio);
    const miRetiro = retiro.map((r) => r * pMia);

    let run = 0;
    const acumulado = miRetiro.map((v, i) => {
      if (i < nVisibles) run += v;
      return run;
    });

    const gastosPct = venta.map((v, i) => safeDiv(S.gastos[i], v));
    const utilPct = venta.map((v, i) => safeDiv(utilidad[i], v));

    // Promedios sobre meses con actividad real (no entre 12).
    const act = (data.activeIdxs || []).filter((i) => i < nVisibles);
    const prom = (arr) => (act.length ? suma(arr, act) / act.length : 0);

    const T = (arr) => suma(arr, visibles);
    const tVenta = T(venta), tGastos = T(S.gastos), tUtil = T(utilidad);

    // MRR = recurrente + anualidades prorrateadas (siempre devengado)
    const mrr = S.recurrente.map((v, i) => v + S.anualidadesDev[i]);
    const mrrPrev = (data.anioAnterior?.series?.recurrente || z12()).map(
      (v, i) => v + (data.anioAnterior.series.anualidadesDev?.[i] || 0)
    );
    const ultMes = Math.min(data.ultimoMesConDatos ?? 0, nVisibles - 1);

    // ── Conciliación caja ↔ devengado sobre los meses que se están viendo ──
    // Es exacta para cualquier ventana:
    //   devengado = caja + entra − sale
    //   entra = anualidades del año pasado que se devengan en estos meses
    //   sale  = anualidades cobradas en estos meses cuya cobertura cae después
    const conc = data.conciliacion || { arrastreAnterior: 0, arrastreSiguiente: 0 };
    const ventaCaja = z12().map(
      (_, i) => S.recurrente[i] + S.anualidades[i] + S.setup[i] + S.otros[i]
    );
    const entra = suma(S.anualidadesDevArrastre || z12(), visibles);
    const sale =
      suma(S.anualidades, visibles) - suma(S.anualidadesDevPropio || z12(), visibles);
    const anticipado = sale;

    const bloques = [
      {
        titulo: "Ingresos",
        color: "#3b82f6",
        rows: [
          { label: "Venta", vals: venta, bold: true },
          { label: "Recurrente", vals: S.recurrente, sub: true },
          { label: "Anualidades", vals: anualidades, sub: true },
          { label: "Setup / proyectos", vals: S.setup, sub: true },
          { label: "Otros", vals: S.otros, sub: true },
        ],
      },
      {
        titulo: "Costos",
        color: "#ef4444",
        rows: [
          { label: "Gastos", vals: S.gastos, bold: true, tono: "rojo" },
          { label: "Gastos %", vals: gastosPct, pct: true,
            total: safeDiv(tGastos, tVenta), promedio: safeDiv(prom(S.gastos), prom(venta)) },
        ],
      },
      {
        titulo: "Resultado",
        color: "#10b981",
        rows: [
          { label: "Utilidad", vals: utilidad, bold: true, tono: "verde" },
          { label: "Utilidad %", vals: utilPct, pct: true,
            total: safeDiv(tUtil, tVenta), promedio: safeDiv(prom(utilidad), prom(venta)) },
        ],
      },
      {
        titulo: data.sociedad.activa
          ? `Caja y reparto · sociedad ${Math.round(pMia * 100)}/${Math.round(pSocio * 100)}`
          : "Caja y retiro · 100% tuyo",
        color: "#8b5cf6",
        nota: devengado ? "Este bloque siempre va en caja real, sin prorratear." : null,
        rows: data.sociedad.activa
          ? [
              { label: "Cobranza", vals: S.cobranza, bold: true },
              { label: "Retiro generado", vals: retiro, bold: true },
              { label: `${data.sociedad.nombreSocio} (${Math.round(pSocio * 100)}%)`,
                vals: socio, sub: true, tono: "rojo" },
              { label: "Mi retiro (50%)", vals: miRetiro, bold: true, tono: "verde", destacar: true },
              { label: "Mi retiro acumulado", vals: acumulado, sub: true, acumulado: true },
            ]
          : [
              { label: "Cobranza", vals: S.cobranza, bold: true },
              { label: "Mi retiro", vals: miRetiro, bold: true, tono: "verde", destacar: true },
              { label: "Mi retiro acumulado", vals: acumulado, sub: true, acumulado: true },
            ],
      },
    ];

    // Total / promedio por fila
    bloques.forEach((b) =>
      b.rows.forEach((r) => {
        if (r.pct) return; // ya vienen calculados
        if (r.acumulado) {
          r.total = r.vals[Math.max(nVisibles - 1, 0)];
          r.promedio = null;
          return;
        }
        r.total = T(r.vals);
        r.promedio = prom(r.vals);
      })
    );

    return {
      bloques, venta, utilidad, retiro, socio, miRetiro, mrr, mrrPrev, ultMes,
      anticipado, conc, entra, sale, tVentaCaja: T(ventaCaja), tVenta, tUtil, tGastos,
      tMiRetiro: T(miRetiro), tSocio: T(socio), tCobranza: T(S.cobranza),
      margen: safeDiv(tUtil, tVenta),
      S, anualidades,
    };
  }, [data, vista, nVisibles, visibles]);

  // ── Datos de gráficas ─────────────────────────────────────────────────────
  const chartMRR = useMemo(() => {
    if (!calc) return [];
    return visibles.map((i) => ({
      mes: MES_CORTO[i],
      Recurrente: Math.round(calc.S.recurrente[i]),
      Anualidades: Math.round(calc.S.anualidadesDev[i]),
      MRR: Math.round(calc.mrr[i]),
      [`MRR ${data.anioAnterior.anio}`]: Math.round(calc.mrrPrev[i]),
    }));
  }, [calc, visibles, data]);

  const chartRetiro = useMemo(() => {
    if (!calc) return [];
    return visibles.map((i) => ({
      mes: MES_CORTO[i],
      "Mi retiro": Math.round(calc.miRetiro[i]),
      Socio: Math.round(calc.socio[i]),
    }));
  }, [calc, visibles]);

  // ── PDF ───────────────────────────────────────────────────────────────────
  function exportarPDF() {
    if (!calc) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const startY = pdfHeader(
      doc,
      `Balance Mensual V2 — ${vista === "caja" ? "Caja" : "Devengado"}`,
      selectedYear
    );

    const head = [["Concepto", ...visibles.map((i) => MES_CORTO[i]), "Total", "Prom."]];
    const body = [];
    const meta = []; // para pintar

    calc.bloques.forEach((b) => {
      body.push([b.titulo.toUpperCase(), ...visibles.map(() => ""), "", ""]);
      meta.push({ tipo: "seccion", color: b.color });
      b.rows.forEach((r) => {
        const fmt = (v) => (r.pct ? fPct(v) : fMXN(v));
        body.push([
          (r.sub ? "   " : "") + r.label,
          ...visibles.map((i) => fmt(r.vals[i])),
          fmt(r.total),
          r.promedio == null ? "—" : fmt(r.promedio),
        ]);
        meta.push({ tipo: "fila", bold: !!r.bold, destacar: !!r.destacar });
      });
    });

    const pageW = doc.internal.pageSize.getWidth();
    const marginL = 8, marginR = 8;
    const disponible = pageW - marginL - marginR;
    const wConcepto = 38, wResumen = 21;
    const wMes = (disponible - wConcepto - wResumen * 2) / visibles.length;
    const columnStyles = { 0: { cellWidth: wConcepto, halign: "left" } };
    visibles.forEach((_, i) => (columnStyles[i + 1] = { cellWidth: wMes, halign: "center" }));
    columnStyles[visibles.length + 1] = { cellWidth: wResumen, halign: "center" };
    columnStyles[visibles.length + 2] = { cellWidth: wResumen, halign: "center" };

    doc.autoTable({
      startY, head, body, columnStyles,
      styles: {
        fontSize: 7, cellPadding: { top: 2.2, right: 2.5, bottom: 2.2, left: 2.5 },
        overflow: "hidden", font: "helvetica",
        lineColor: [226, 232, 240], lineWidth: 0.25,
      },
      headStyles: {
        fillColor: [15, 23, 42], textColor: [255, 255, 255],
        fontStyle: "bold", fontSize: 6.8, halign: "center",
      },
      alternateRowStyles: { fillColor: [252, 252, 253] },
      didParseCell: (d) => {
        if (d.row.section !== "body") return;
        const m = meta[d.row.index];
        if (!m) return;
        if (m.tipo === "seccion") {
          d.cell.styles.fillColor = [241, 245, 249];
          d.cell.styles.textColor = [51, 65, 85];
          d.cell.styles.fontStyle = "bold";
          d.cell.styles.fontSize = 6.5;
        } else {
          if (m.bold) d.cell.styles.fontStyle = "bold";
          if (m.destacar) d.cell.styles.fillColor = [236, 253, 245];
        }
      },
      margin: { left: marginL, right: marginR },
      tableWidth: disponible,
    });

    // Nota al pie explicando la vista
    const y = doc.lastAutoTable.finalY + 5;
    doc.setFontSize(7).setTextColor(120);
    const nota = vista === "devengado"
      ? "Devengado: las anualidades se reparten entre los meses que cubren, aunque pasen al ano siguiente. Cobranza, gastos y retiro siempre van en caja real."
      : "Caja: el dinero como entró. Enero incluye las anualidades cobradas por adelantado.";
    doc.text(nota, marginL, y);

    let yExtra = y;
    if (vista === "devengado" && (calc.entra > 0 || calc.sale > 0)) {
      yExtra += 4;
      doc.text(
        `Conciliacion: venta en caja ${fMXN(calc.tVentaCaja)} + ${fMXN(calc.entra)} cobrado en ${Number(selectedYear) - 1} que se devenga aqui`
        + ` - ${fMXN(calc.sale)} cobrado aqui que cubre meses posteriores = ${fMXN(calc.tVenta)}.`,
        marginL, yExtra
      );
    }

    if (data?.sociedad?.activa) {
      doc.text(
        `Sociedad vigente en ${selectedYear}: el retiro se reparte al 50% con ${data.sociedad.nombreSocio}.`,
        marginL, yExtra + 4
      );
    }
    pdfFooter(doc);
    doc.save(`Balance_Mensual_V2_${selectedYear}_${vista}.pdf`);
  }

  // ── Excel ─────────────────────────────────────────────────────────────────
  async function exportarExcel() {
    if (!calc) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(`Balance ${selectedYear}`);

    ws.addRow([`Balance Mensual V2 — ${selectedYear}`]);
    ws.addRow([vista === "caja"
      ? "Vista Caja: el dinero como entró (enero incluye anualidades)."
      : `Vista Devengado: anualidades repartidas entre los meses que cubren, aunque pasen de año. `
        + `Caja ${fMXN(calc.tVentaCaja)} + ${fMXN(calc.entra)} cobrado en ${Number(selectedYear) - 1} que se devenga aquí`
        + ` − ${fMXN(calc.sale)} cobrado aquí que cubre meses posteriores = ${fMXN(calc.tVenta)}.`]);
    ws.addRow([data.sociedad.activa
      ? `Sociedad vigente: el retiro se reparte 50/50 con ${data.sociedad.nombreSocio}.`
      : "Sin sociedad: el retiro es 100% tuyo."]);
    ws.addRow([]);
    ws.getRow(1).font = { bold: true, size: 13 };
    ws.getRow(2).font = { italic: true, size: 9, color: { argb: "FF64748B" } };
    ws.getRow(3).font = { italic: true, size: 9, color: { argb: "FF64748B" } };

    const encabezado = ["Concepto", ...visibles.map((i) => MES_CORTO[i]), "Total", "Promedio"];
    const rHead = ws.addRow(encabezado);
    rHead.font = { bold: true, color: { argb: "FFFFFFFF" } };
    rHead.alignment = { horizontal: "center" };
    rHead.eachCell((c) => {
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F172A" } };
    });

    ws.getColumn(1).width = 30;
    for (let c = 2; c <= encabezado.length; c++) ws.getColumn(c).width = 13;

    const money = '"$"#,##0;[Red]-"$"#,##0';
    const pct = "0.0%";

    calc.bloques.forEach((b) => {
      const rs = ws.addRow([b.titulo.toUpperCase()]);
      rs.font = { bold: true, color: { argb: "FF334155" } };
      rs.eachCell((c) => {
        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
      });
      ws.mergeCells(rs.number, 1, rs.number, encabezado.length);

      b.rows.forEach((r) => {
        const fila = ws.addRow([
          (r.sub ? "    " : "") + r.label,
          ...visibles.map((i) => Number(r.vals[i]) || 0),
          Number(r.total) || 0,
          r.promedio == null ? null : Number(r.promedio) || 0,
        ]);
        if (r.bold) fila.font = { bold: true };
        for (let c = 2; c <= encabezado.length; c++) {
          fila.getCell(c).numFmt = r.pct ? pct : money;
          fila.getCell(c).alignment = { horizontal: "center" };
        }
        if (r.destacar) {
          fila.eachCell((c) => {
            c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFECFDF5" } };
          });
        }
      });
    });

    // Hoja de anualidades
    if (data.anualidades?.length) {
      const ws2 = wb.addWorksheet("Anualidades");
      ws2.addRow(["Cliente", "Descripción", "Fecha cobro", "Importe", "Cubre",
                  "Meses", "Por mes", `En ${selectedYear}`, "Origen"])
        .font = { bold: true };
      [30, 34, 13, 14, 20, 8, 14, 14, 18].forEach((w, i) => (ws2.getColumn(i + 1).width = w));
      data.anualidades.forEach((a) => {
        const f = ws2.addRow([
          a.cliente, a.descripcion, a.fecha, a.importe,
          `${MES_CORTO[a.mesIni]} ${a.anioIni} – ${MES_CORTO[a.mesFin]} ${a.anioFin}`,
          a.meses, a.porMes, a.enEsteAnio,
          a.arrastre ? `Cobrado en ${a.anioCargo}` : "Cobrado este año",
        ]);
        [4, 7, 8].forEach((c) => (f.getCell(c).numFmt = money));
      });
    }

    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `BalanceMensualV2_${selectedYear}_${vista}.xlsx`
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const cellBase = { textAlign: "center", whiteSpace: "nowrap", fontSize: "0.76rem" };
  const tono = (t) => (t === "rojo" ? "#dc2626" : t === "verde" ? "#059669" : "#0f172a");

  return (
    <>
      <Header />
      {user.menu_usuarios ? (
        <div className="card container col-sm-12" style={{ paddingBottom: 24 }}>

          {/* Encabezado */}
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <IcBalance style={{ marginRight: 8, color: "#8b5cf6" }} />
                Balance Mensual V2
                <span style={{
                  marginLeft: 10, fontSize: "0.6rem", fontWeight: 700,
                  padding: "3px 8px", borderRadius: 999, verticalAlign: "middle",
                  background: data?.sociedad?.activa ? "#fef3c7" : "#dcfce7",
                  color: data?.sociedad?.activa ? "#92400e" : "#166534",
                }}>
                  {data?.sociedad?.activa
                    ? `SOCIEDAD 50/50 · ${data.sociedad.nombreSocio}`
                    : "RETIRO 100% TUYO"}
                </span>
              </h3>
              <p className="page-subtitle">
                Ingreso recurrente, reparto de retiro y anualidades normalizadas
              </p>
            </div>
            <div className="page-actions">
              <ButtonGroup>
                <Button size="sm" color="danger" onClick={exportarPDF}>
                  <IcPdf style={{ marginRight: 4 }} /> PDF
                </Button>
                <Button size="sm" color="primary" onClick={exportarExcel}>
                  <IcExcel style={{ marginRight: 4 }} /> Excel
                </Button>
              </ButtonGroup>
            </div>
          </div>

          {/* Filtros */}
          <div className="filter-bar">
            <div>
              <Label>Año</Label>
              <Input bsSize="sm" type="select" style={{ minWidth: 100 }}
                value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                {anios.map((y) => <option key={y} value={y}>{y}</option>)}
              </Input>
            </div>
            <div>
              <Label>Vista</Label>
              <ButtonGroup size="sm" style={{ display: "block" }}>
                <Button color={vista === "devengado" ? "primary" : "secondary"}
                  outline={vista !== "devengado"} onClick={() => setVista("devengado")}>
                  Devengado
                </Button>
                <Button color={vista === "caja" ? "primary" : "secondary"}
                  outline={vista !== "caja"} onClick={() => setVista("caja")}>
                  Caja
                </Button>
              </ButtonGroup>
            </div>
            {esAnioEnCurso && (
              <div>
                <Label>Mes en curso en promedios</Label>
                <Input bsSize="sm" type="select" style={{ minWidth: 110 }}
                  value={incluirMesActual ? "si" : "no"}
                  onChange={(e) => setIncluirMesActual(e.target.value === "si")}>
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </Input>
              </div>
            )}
          </div>

          {/* Explicación de la vista */}
          <div style={{
            display: "flex", gap: 8, alignItems: "flex-start",
            background: vista === "devengado" ? "#eff6ff" : "#fffbeb",
            border: `1px solid ${vista === "devengado" ? "#bfdbfe" : "#fde68a"}`,
            borderRadius: 8, padding: "10px 14px", margin: "0 0 16px",
            fontSize: "0.75rem", color: "#475569", lineHeight: 1.45,
          }}>
            <IcInfo style={{ color: vista === "devengado" ? "#3b82f6" : "#d97706", flexShrink: 0, marginTop: 2 }} />
            <div>
              {vista === "devengado" ? (
                <>
                  <b>Devengado.</b> Las anualidades se reparten entre los meses que cubren, aunque
                  se pasen al año siguiente: así enero deja de estar inflado y ves el ritmo real del
                  negocio. Por eso el total no empata con Caja — la diferencia está desglosada abajo
                  de la tabla. <b>Cobranza, gastos y retiro nunca se prorratean</b>: ese bloque
                  siempre va en dinero real.
                </>
              ) : (
                <>
                  <b>Caja.</b> El dinero como entró. Enero carga las anualidades completas, por eso
                  se dispara. Es la vista correcta para conciliar contra banco y para calcular retiros.
                </>
              )}
            </div>
          </div>

          {/* KPIs */}
          {calc && (
            <div className="row" style={{ margin: "0 0 18px" }}>
              {[
                {
                  t: "MRR", v: fMXN(calc.mrr[calc.ultMes]),
                  s: `ARR ${fMXN(calc.mrr[calc.ultMes] * 12)} · ${MES_CORTO[calc.ultMes]}`,
                  c: "#3b82f6",
                },
                {
                  t: "Venta del año", v: fMXN(calc.tVenta),
                  s: `Margen ${fPct(calc.margen)}`, c: "#6366f1",
                },
                {
                  t: "Mi retiro del año", v: fMXN(calc.tMiRetiro),
                  s: data.sociedad.activa
                    ? `${data.sociedad.nombreSocio} se llevó ${fMXN(calc.tSocio)}`
                    : "Sin socio · retiro completo",
                  c: "#059669",
                },
                {
                  t: "Cobrado, por devengar", v: fMXN(calc.anticipado),
                  s: calc.anticipado > 0
                    ? `Cubre meses posteriores a ${MES_CORTO[nVisibles - 1]}${
                        calc.conc.arrastreSiguiente > 0
                          ? ` · ${fMXN(calc.conc.arrastreSiguiente)} caen en ${Number(selectedYear) + 1}`
                          : ""
                      }`
                    : "Nada pendiente de devengar",
                  c: "#d97706",
                },
              ].map((k) => (
                <div key={k.t} className="col-6 col-lg-3 mb-2" style={{ padding: "0 6px" }}>
                  <div style={{
                    background: "#fff", border: "1px solid #e2e8f0",
                    borderTop: `3px solid ${k.c}`, borderRadius: 10,
                    padding: "12px 14px", height: "100%",
                  }}>
                    <div style={{
                      fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.06em",
                      textTransform: "uppercase", color: "#94a3b8",
                    }}>{k.t}</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: k.c, margin: "2px 0" }}>
                      {k.v}
                    </div>
                    <div style={{ fontSize: "0.68rem", color: "#64748b" }}>{k.s}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tabla */}
          {calc && (
            <div style={{ overflowX: "auto" }}>
              <Table bordered size="sm" responsive={false} className="balance-table table-dark-header">
                <thead>
                  <tr>
                    <th style={{ minWidth: 175, width: 175 }}>Concepto</th>
                    {visibles.map((i) => (
                      <th key={i} style={{ textAlign: "center" }}>{MES_CORTO[i]}</th>
                    ))}
                    <th style={{ textAlign: "center" }}>Total</th>
                    <th style={{ textAlign: "center" }}>Promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {calc.bloques.map((b) => (
                    <React.Fragment key={b.titulo}>
                      <tr>
                        <td colSpan={visibles.length + 3} style={{
                          background: `${b.color}12`, borderLeft: `3px solid ${b.color}`,
                          fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.07em",
                          textTransform: "uppercase", color: b.color, padding: "5px 10px",
                        }}>
                          {b.titulo}
                          {b.nota && (
                            <span style={{
                              marginLeft: 8, fontWeight: 500, textTransform: "none",
                              letterSpacing: 0, color: "#64748b", fontSize: "0.66rem",
                            }}>· {b.nota}</span>
                          )}
                        </td>
                      </tr>
                      {b.rows.map((r) => {
                        const fmt = (v) => (r.pct ? fPct(v) : fMXN(v));
                        return (
                          <tr key={r.label} style={r.destacar ? { background: "#ecfdf5" } : undefined}>
                            <td style={{
                              fontWeight: r.bold ? 700 : 400,
                              paddingLeft: r.sub ? 26 : 10,
                              color: r.sub ? "#64748b" : "#0f172a",
                              fontSize: "0.76rem",
                              whiteSpace: "normal",
                            }}>
                              {r.label}
                            </td>
                            {visibles.map((i) => (
                              <td key={i} style={{
                                ...cellBase,
                                fontWeight: r.bold ? 700 : 400,
                                color: r.bold || r.tono ? tono(r.tono) : "#334155",
                              }}>
                                {fmt(r.vals[i])}
                              </td>
                            ))}
                            <td style={{
                              ...cellBase, background: "#f2f2f2",
                              fontWeight: 700, color: tono(r.tono),
                            }}>
                              {fmt(r.total)}
                            </td>
                            <td style={{ ...cellBase, background: "#f2f2f2", color: "#475569" }}>
                              {r.promedio == null ? "—" : fmt(r.promedio)}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Conciliación caja ↔ devengado */}
          {calc && vista === "devengado" && (calc.entra > 0 || calc.sale > 0) && (
            <div style={{
              display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6,
              background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
              padding: "10px 14px", marginTop: 12, fontSize: "0.74rem", color: "#475569",
            }}>
              <span style={{ fontWeight: 700, color: "#0f172a", marginRight: 4 }}>
                Conciliación:
              </span>
              <span>
                Venta en caja {MES_CORTO[0]}–{MES_CORTO[nVisibles - 1]}{" "}
                <b>{fMXN(calc.tVentaCaja)}</b>
              </span>
              <span style={{ color: "#059669", fontWeight: 600 }}>+ {fMXN(calc.entra)}</span>
              <span style={{ color: "#94a3b8" }}>
                cobrado en {Number(selectedYear) - 1} que se devenga aquí
              </span>
              <span style={{ color: "#dc2626", fontWeight: 600 }}>− {fMXN(calc.sale)}</span>
              <span style={{ color: "#94a3b8" }}>
                cobrado aquí que cubre meses posteriores
              </span>
              <span style={{ marginLeft: "auto", fontWeight: 700, color: "#0f172a" }}>
                = venta devengada {fMXN(calc.tVenta)}
              </span>
            </div>
          )}

          {/* Gráficas */}
          {calc && (
            <div className="row" style={{ marginTop: 22 }}>
              <div className="col-12 col-xl-7 mb-3">
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, background: "#fff" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a" }}>
                    Ingreso recurrente mensual (MRR)
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#94a3b8", marginBottom: 10 }}>
                    Mensualidades + anualidades prorrateadas · comparado contra {data.anioAnterior.anio}
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={chartMRR} margin={{ top: 4, right: 8, left: -6, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fCorto} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => fMXN(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Recurrente" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} isAnimationActive={false} />
                      <Bar dataKey="Anualidades" stackId="a" fill="#93c5fd" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                      <Line type="monotone" dataKey={`MRR ${data.anioAnterior.anio}`}
                        stroke="#cbd5e1" strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
                      <Line type="monotone" dataKey="MRR" stroke="#f59e0b" strokeWidth={2.5}
                        dot={{ r: 3, fill: "#f59e0b" }} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="col-12 col-xl-5 mb-3">
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, background: "#fff" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a" }}>
                    Retiro mensual
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#94a3b8", marginBottom: 10 }}>
                    {data.sociedad.activa
                      ? `Reparto 50/50 con ${data.sociedad.nombreSocio}`
                      : "Retiro completo — sin socio"}
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={chartRetiro} margin={{ top: 4, right: 8, left: -6, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fCorto} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => fMXN(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Mi retiro" stackId="r" fill="#10b981" radius={data.sociedad.activa ? [0,0,0,0] : [4,4,0,0]} isAnimationActive={false} />
                      {data.sociedad.activa && (
                        <Bar dataKey="Socio" stackId="r" fill="#fbbf24" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Detalle de anualidades */}
          {data?.anualidades?.length > 0 && (
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff", marginTop: 4 }}>
              <button
                onClick={() => setVerAnualidades((v) => !v)}
                style={{
                  width: "100%", background: "none", border: "none", textAlign: "left",
                  padding: "12px 16px", display: "flex", alignItems: "center", gap: 8,
                  cursor: "pointer", color: "#0f172a",
                }}
              >
                {verAnualidades ? <IcChevU /> : <IcChevD />}
                <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                  Anualidades detectadas en {selectedYear}
                </span>
                <span style={{
                  background: "#fef3c7", color: "#92400e", borderRadius: 999,
                  padding: "2px 9px", fontSize: "0.68rem", fontWeight: 700,
                }}>
                  {data.anualidades.length} · {fMXN(data.anualidades.reduce((a, b) => a + b.enEsteAnio, 0))} en {selectedYear}
                </span>
              </button>

              {verAnualidades && (
                <div style={{ padding: "0 16px 14px" }}>
                  <p style={{ fontSize: "0.72rem", color: "#64748b", marginBottom: 10 }}>
                    Se considera anualidad todo cargo cuya descripción dice “anual” o que equivale
                    a 3 o más meses de la renta del cliente. Se reparte entre los meses que indica
                    la descripción; si no dice, desde el mes en que se cobró hasta diciembre. Los
                    renglones con <b>↩</b> son cobros de {Number(selectedYear) - 1} cuya cobertura
                    alcanza a {selectedYear}; cuando la columna <i>En {selectedYear}</i> es menor al
                    importe, el resto se devenga en otro año.
                  </p>
                  <Table size="sm" bordered responsive className="table-dark-header">
                    <thead>
                      <tr>
                        <th>Cliente</th><th>Descripción</th><th>Cobrado</th>
                        <th style={{ textAlign: "right" }}>Importe</th>
                        <th style={{ textAlign: "center" }}>Cubre</th>
                        <th style={{ textAlign: "right" }}>Por mes</th>
                        <th style={{ textAlign: "right" }}>En {selectedYear}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.anualidades.map((a, i) => (
                        <tr key={i} style={a.arrastre ? { background: "#fffbeb" } : undefined}>
                          <td style={{ fontWeight: 600, fontSize: "0.76rem" }}>
                            {a.arrastre && (
                              <span title={`Cobrado en ${a.anioCargo}`}
                                style={{ color: "#d97706", marginRight: 5, fontWeight: 700 }}>↩</span>
                            )}
                            {a.cliente}
                          </td>
                          <td style={{ fontSize: "0.74rem", color: "#64748b" }}>{a.descripcion}</td>
                          <td style={{ fontSize: "0.74rem" }}>{moment(a.fecha).format("DD/MM/YYYY")}</td>
                          <td style={{ textAlign: "right", fontWeight: 600, fontSize: "0.76rem" }}>{fMXN(a.importe)}</td>
                          <td style={{ textAlign: "center", fontSize: "0.72rem" }}>
                            <span style={{
                              background: "#eff6ff", color: "#1d4ed8", borderRadius: 6,
                              padding: "2px 7px", fontWeight: 600,
                            }}>
                              {MES_CORTO[a.mesIni]} {String(a.anioIni).slice(2)} – {MES_CORTO[a.mesFin]} {String(a.anioFin).slice(2)} · {a.meses}m
                            </span>
                          </td>
                          <td style={{ textAlign: "right", color: "#059669", fontWeight: 600, fontSize: "0.76rem" }}>
                            {fMXN(a.porMes)}
                          </td>
                          <td style={{
                            textAlign: "right", fontSize: "0.76rem", fontWeight: 600,
                            color: Math.round(a.enEsteAnio) === Math.round(a.importe) ? "#334155" : "#d97706",
                          }}>
                            {fMXN(a.enEsteAnio)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {!data && (
            <p style={{ color: "#94a3b8", fontSize: "0.82rem", padding: 20 }}>
              Cargando balance…
            </p>
          )}
        </div>
      ) : undefined}
    </>
  );
}

export default BalanceMensualV2;
